import { useMutation, useQuery } from '@tanstack/react-query';

import { getProductsQueryOptions, postUserProductMutationFn } from '@/queries/products';
import { trackMP } from '@/shared/mp';

import { useTourContext } from '../../context';
import { BannerButton } from '../../types';
import GuidedTourBanner from './GuidedTourBanner';

interface OfferCheckoutBannerProps {
  speechBubble?: string;
  buttonLabel: string;
  /** Offer id applied at checkout (case-insensitive; lowercased before the request). */
  offerId: string;
  mascotSrc: string;
  /** The runner's button handler, used to mark the tour complete + track before redirecting. */
  onButtonClick: (button: BannerButton) => void;
}

/**
 * The "eligible + a locked habitat to offer" ending of the habitat onboarding. Renders through the
 * standard guided-tour banner shell, but its single button buys the recommended habitat's
 * single-habitat-day-pass with the offer applied and redirects straight to Stripe — the one ending
 * a declarative navigate button cannot express. Keeping the product lookup and checkout here leaves
 * the tour core free of product/Stripe concepts.
 */
const OfferCheckoutBanner = ({ speechBubble, buttonLabel, offerId, mascotSrc, onButtonClick }: OfferCheckoutBannerProps) => {
  const recommended = useTourContext()?.recommendedHabitat;

  const { data: product } = useQuery({
    ...getProductsQueryOptions({ habitatId: recommended?._id, lookupKey: 'single-habitat-day-pass' }),
    select: (data) => data.list[0],
    enabled: !!recommended?._id,
  });

  const { mutate: checkout, isPending } = useMutation({
    mutationFn: postUserProductMutationFn,
    onSuccess: (data) => {
      // Complete + track before leaving, so returning from a cancelled Stripe session does not
      // re-show the onboarding. Assigning `location.href` still runs even as the tour unmounts.
      onButtonClick({ label: buttonLabel, completeTour: true, trackAction: 'claimed-offer' });
      window.location.href = data.url;
    },
  });

  const onClick = () => {
    if (!recommended || !product || isPending) return;
    checkout({
      productId: product._id,
      priceId: product.price.priceId,
      associatedHabitatIds: [recommended._id],
      successPath: `/h/${recommended.zooSlug}/${recommended.slug}`,
      cancelPath: window.location.pathname,
      offerId: offerId.toLowerCase(),
    });
    trackMP('request-checkout', {
      productId: product._id,
      priceId: product.price.priceId,
      productLookupKey: product.productLookupKey,
      habitatId: recommended._id,
      offerId: offerId.toLowerCase(),
      entryPoint: 'onboarding-offer',
    });
  };

  return <GuidedTourBanner message={speechBubble} mascotSrc={mascotSrc} buttons={[{ label: buttonLabel, onClick }]} />;
};

export default OfferCheckoutBanner;
